import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, Trash2, Building2, ChevronRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { EmptyState, PageLoading } from "@/components/shared";
import { Field, useReadOnly } from "./common";
import api from "@/lib/api";
import { CompBadge, DocBadge, StatusBadge } from "./scBadges";
import { fmtDate } from "@/components/shared";

const empty = { legal_name: "", trading_name: "", country: "", company_registration_number: "", vat_id: "", tax_number: "", street: "", postal_code: "", city: "", contact_person: "", email: "", phone: "", website: "", notes: "" };

export default function SubCompanies() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const readOnly = useReadOnly();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [next, setNext] = useState(null);
  const load = () => api.get(`/subcon/companies?q=${encodeURIComponent(q)}`).then((r) => setRows(r.data.companies));
  useEffect(() => { const tm = setTimeout(load, 200); return () => clearTimeout(tm); }, [q]);
  const doDelete = async () => { try { await api.delete(`/subcon/companies/${del.id}`); toast.success(t("sub.deleted")); setDel(null); load(); } catch { toast.error("Error"); } };

  return (
    <div data-testid="sub-companies">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-md flex-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("sub.coSearch")} className="pl-9" data-testid="sub-company-search" /></div>
        {!readOnly && <Button onClick={() => setOpen(true)} data-testid="add-company-btn" className="rounded-full bg-navy text-white hover:bg-navy-muted"><Plus className="mr-1 h-4 w-4" /> {t("sub.coAdd")}</Button>}
      </div>
      {rows === null ? <PageLoading /> : rows.length === 0 ? <EmptyState icon={Building2} title={t("sub.coEmpty")} subtitle={t("sub.coEmptyHint")} /> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="sub-companies-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">{t("sub.coName")}</th><th className="px-4 py-3">{t("sub.fCountry")}</th><th className="px-4 py-3">{t("sub.fVat")}</th><th className="px-4 py-3">{t("sub.fCity")}</th><th className="px-4 py-3">{t("sub.coWorkers")}</th><th className="px-4 py-3">{t("sc.colSites")}</th><th className="px-4 py-3">{t("sc.colContract")}</th><th className="px-4 py-3">{t("sc.colA1")}</th><th className="px-4 py-3">{t("sc.colFreist")}</th><th className="px-4 py-3">{t("sc.colCompliance")}</th><th className="px-4 py-3">{t("sc.colNext")}</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" onClick={() => nav(`/app/employer/subcontractors/companies/${c.id}`)} data-testid={`company-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{c.trading_name || c.legal_name}{c.trading_name && <div className="text-xs font-normal text-slate-400">{c.legal_name}</div>}</td>
                  <td className="px-4 py-3 text-slate-600">{c.country || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.vat_id || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.city || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.worker_count}</td>
                  <td className="px-4 py-3 text-slate-600">{c.active_sites || 0}</td>
                  <td className="px-4 py-3"><StatusBadge v={c.contract_status} testid={`company-contract-${c.id}`} /></td>
                  <td className="px-4 py-3"><DocBadge v={c.a1_status} testid={`company-a1-${c.id}`} /></td>
                  <td className="px-4 py-3"><DocBadge v={c.freistellung_status} testid={`company-freist-${c.id}`} /></td>
                  <td className="px-4 py-3"><CompBadge v={c.compliance} testid={`company-compliance-${c.id}`} /></td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(c.next_expiry) || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!readOnly && <button onClick={(e) => { e.stopPropagation(); setDel(c); }} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`company-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>}
                      <ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CompanyWizard open={open} onClose={() => setOpen(false)} onCreated={(id) => { setOpen(false); load(); setNext(id); }} t={t} />
      <Dialog open={!!next} onOpenChange={(o) => !o && setNext(null)}>
        <DialogContent className="max-w-md" data-testid="company-next-step">
          <DialogHeader><DialogTitle>{t("sc.setupNext")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">{t("sc.setupHint")}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setNext(null)} data-testid="next-step-later">{t("sc.later")}</Button>
            <Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" onClick={() => nav(`/app/employer/subcontractors/contracts?new=1&sub=${next}`)} data-testid="next-step-setup">{t("sc.setupCta")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="delete-company-dialog">
          <AlertDialogHeader><AlertDialogTitle>{t("sub.coDelete")}</AlertDialogTitle><AlertDialogDescription>{t("sub.coDeleteConfirm", { name: del?.legal_name || "" })}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>{t("det.cancel")}</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-company">{t("sub.coDelete")}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CompanyWizard({ open, onClose, onCreated, t }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const reset = () => { setStep(1); setF(empty); };
  const submit = async () => {
    if (!f.legal_name.trim()) { toast.error(t("sub.fLegalName") + " *"); setStep(1); return; }
    setBusy(true);
    try { const { data } = await api.post("/subcon/companies", f); toast.success(t("sub.added")); reset(); onCreated(data.id); }
    catch { toast.error("Error"); } finally { setBusy(false); }
  };
  const titles = { 1: t("sub.wizCompany"), 2: t("sub.wizContact"), 3: t("sub.wizTax") };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="company-wizard">
        <DialogHeader><DialogTitle>{t("sub.coAdd")} — {titles[step]} <span className="text-sm font-normal text-slate-400">({step}/3)</span></DialogTitle></DialogHeader>
        {step === 1 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Field label={t("sub.fLegalName")} required value={f.legal_name} onChange={set("legal_name")} testid="wiz-legal-name" /></div>
            <Field label={t("sub.fTradingName")} value={f.trading_name} onChange={set("trading_name")} testid="wiz-trading-name" />
            <Field label={t("sub.fCountry")} value={f.country} onChange={set("country")} testid="wiz-country" />
            <Field label={t("sub.fRegNo")} value={f.company_registration_number} onChange={set("company_registration_number")} testid="wiz-regno" />
            <Field label={t("sub.fVat")} value={f.vat_id} onChange={set("vat_id")} testid="wiz-vat" />
            <div className="col-span-2"><Field label={t("sub.fStreet")} value={f.street} onChange={set("street")} testid="wiz-street" /></div>
            <Field label={t("sub.fPostal")} value={f.postal_code} onChange={set("postal_code")} testid="wiz-postal" />
            <Field label={t("sub.fCity")} value={f.city} onChange={set("city")} testid="wiz-city" />
          </div>
        )}
        {step === 2 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Field label={t("sub.fContact")} value={f.contact_person} onChange={set("contact_person")} testid="wiz-contact" /></div>
            <Field label={t("sub.fEmail")} type="email" value={f.email} onChange={set("email")} testid="wiz-email" />
            <Field label={t("sub.fPhone")} value={f.phone} onChange={set("phone")} testid="wiz-phone" />
            <div className="col-span-2"><Field label={t("sub.fWebsite")} value={f.website} onChange={set("website")} testid="wiz-website" /></div>
          </div>
        )}
        {step === 3 && (
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("sub.fTaxNo")} value={f.tax_number} onChange={set("tax_number")} testid="wiz-taxno" />
            <div className="col-span-2"><Field label={t("sub.fNotes")} value={f.notes} onChange={set("notes")} testid="wiz-notes" /></div>
            <p className="col-span-2 text-xs text-slate-400">{t("sub.wizDocsNote")}</p>
          </div>
        )}
        <DialogFooter className="gap-2">
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wiz-back" className="rounded-full">{t("sub.back")}</Button>}
          {step < 3 && <Button onClick={() => setStep(step + 1)} data-testid="wiz-next" className="rounded-full bg-navy text-white hover:bg-navy-muted">{t("sub.next")}</Button>}
          {step === 3 && <Button onClick={submit} disabled={busy} data-testid="wiz-save" className="rounded-full bg-cyan text-white hover:bg-cyan-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sub.save")}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
